import { useCallback, useRef } from "react";
import {
  isActivationPending,
  getCompletedStepsFromStorage,
} from "../Utils/onboardingNavigation";
import { confirmActivationState } from "../Utils/activationSync";
import { clearActivationSessionId } from "../Services/Chat";

/**
 * Refresh the cached profile when the activation conversation ends.
 *
 * Activation finishes server-side inside `ActivationService.complete()`, on a
 * tool call the member never sees, and nothing is pushed to the client —
 * `activation.completed` goes onto the platform event bus, not to a device. So
 * the client has to ask, and the question is only *when*.
 *
 * **Once, when the conversation ends.** Not on a timer, and not after every
 * reply. Nothing on either conversation screen reacts to activation state while
 * a session is running — the tabs that care are behind `HomeTabBar`, which the
 * member cannot see mid-session — so an earlier answer buys nothing and a poll
 * spends a request every 30s to find out something that only matters once the
 * member leaves.
 *
 * Ending the conversation is also the *right* moment: activation completes on
 * the closing turn, so by the time the member hangs up the server has stamped
 * it. If a slow write loses that race, `HomeTabBar` asks again on the next
 * navigation and persists the answer — and because it persists, that check
 * stops firing once it succeeds. One call here, one backstop, then silence.
 *
 * Returns `{ onConversationEnd }` — call it when the voice session disconnects
 * or the chat screen unmounts. Cheap to call unconditionally: it reads the
 * cached profile first and does nothing unless activation is actually pending.
 */
export default function useActivationCompletion() {
  // Guards a second call: the chat unmount path and a navigation can both fire.
  const checkingRef = useRef(false);
  const completedRef = useRef(false);

  const onConversationEnd = useCallback(async () => {
    if (completedRef.current || checkingRef.current) return;

    // Read the cache at call time rather than holding it in state: another
    // surface may have refreshed the profile since this screen mounted, and
    // this way a member who was never activating costs nothing at all.
    if (!isActivationPending(getCompletedStepsFromStorage())) return;

    checkingRef.current = true;
    try {
      // null (gate unreachable) or true (still pending) both mean: leave the
      // cache alone. The next navigation asks again.
      if ((await confirmActivationState()) !== false) return;
      completedRef.current = true;
      clearActivationSessionId();
    } finally {
      checkingRef.current = false;
    }
  }, []);

  return { onConversationEnd };
}
